import { useCallback, useState } from "react";

/**
 * A hook that behaves exactly like `useState`, but accepts
 * a callback argument that is invoked whenever state is
 * updated.
 *
 * @example
 * const [state, setState] = useState(0, (newValue) => onChange(newValue));
 */
export function useStateWithCallback<T>(
  initialState: T,
  callback?: (value: T) => void,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(initialState);

  const setStateWithCallback = useCallback(
    (state: T | ((prev: T) => T)) => {
      setState((prev) => {
        const nextState =
          typeof state === "function" ? (state as (prev: T) => T)(prev) : state;

        if (callback) {
          queueMicrotask(() => {
            callback(nextState);
          });
        }

        return nextState;
      });
    },
    [callback],
  );

  return [state, setStateWithCallback];
}
